/**
 * Groups Management Page
 *
 * Allows users to create and manage groups for sharing expressions.
 * Features: create groups, invite members, accept/reject invitations,
 * leave groups, and transfer ownership.
 */

import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import { getAccessToken } from '../lib/supabase';

interface GroupMember {
  userId: string;
  email: string;
  role: 'member' | 'admin' | 'owner';
  joinedAt: string;
}

interface Group {
  id: string;
  name: string;
  description?: string;
  memberCount: number;
  createdAt: string;
  role: 'member' | 'admin' | 'owner';
}

interface Invitation {
  id: string;
  groupId: string;
  groupName: string;
  invitedBy: string;
  inviterEmail: string;
  createdAt: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'https://api.aigentswork.com';

async function fetchGroups(): Promise<Group[]> {
  const token = await getAccessToken();
  if (!token) return [];

  const response = await fetch(`${API_BASE}/api/groups`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) return [];
  return response.json();
}

async function fetchInvitations(): Promise<Invitation[]> {
  const token = await getAccessToken();
  if (!token) return [];

  const response = await fetch(`${API_BASE}/api/invitations`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) return [];
  return response.json();
}

async function fetchGroupMembers(groupId: string): Promise<GroupMember[]> {
  const token = await getAccessToken();
  if (!token) return [];

  const response = await fetch(`${API_BASE}/api/groups/${groupId}/members`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) return [];
  return response.json();
}

const Groups: Component = () => {
  const [groups, { refetch: refetchGroups }] = createResource(fetchGroups);
  const [invitations, { refetch: refetchInvitations }] = createResource(fetchInvitations);
  const [showCreate, setShowCreate] = createSignal(false);
  const [showInvite, setShowInvite] = createSignal<string | null>(null);
  const [showTransfer, setShowTransfer] = createSignal<string | null>(null);
  const [selectedGroupMembers, setSelectedGroupMembers] = createSignal<GroupMember[]>([]);
  const [newGroupName, setNewGroupName] = createSignal('');
  const [newGroupDesc, setNewGroupDesc] = createSignal('');
  const [inviteEmail, setInviteEmail] = createSignal('');
  const [transferUserId, setTransferUserId] = createSignal('');
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const createGroup = async () => {
    setError(null);
    setLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/groups`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newGroupName(),
          description: newGroupDesc() || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create group');
      }

      setShowCreate(false);
      setNewGroupName('');
      setNewGroupDesc('');
      refetchGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (id: string) => {
    if (
      !confirm(
        'Are you sure you want to delete this group? All members will lose access to shared expressions.',
      )
    ) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/groups/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error('Failed to delete group');
      refetchGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete group');
    }
  };

  const leaveGroup = async (id: string) => {
    if (!confirm('Are you sure you want to leave this group?')) {
      return;
    }

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/groups/${id}/leave`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to leave group');
      }
      refetchGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave group');
    }
  };

  const inviteMember = async () => {
    const groupId = showInvite();
    if (!groupId) return;

    setError(null);
    setLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/groups/${groupId}/invitations`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: inviteEmail() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send invitation');
      }

      setShowInvite(null);
      setInviteEmail('');
      alert('Invitation sent successfully!');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const acceptInvitation = async (invitationId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/invitations/${invitationId}/accept`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to accept invitation');
      }

      refetchInvitations();
      refetchGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    }
  };

  const rejectInvitation = async (invitationId: string) => {
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/invitations/${invitationId}/reject`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject invitation');
      }

      refetchInvitations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject invitation');
    }
  };

  const openTransferModal = async (groupId: string) => {
    const members = await fetchGroupMembers(groupId);
    setSelectedGroupMembers(members.filter((m) => m.role !== 'owner'));
    setShowTransfer(groupId);
  };

  const transferOwnership = async () => {
    const groupId = showTransfer();
    if (!groupId || !transferUserId()) return;

    if (!confirm('Are you sure you want to transfer ownership? You will become an admin.')) {
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');

      const response = await fetch(`${API_BASE}/api/groups/${groupId}/transfer-ownership`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ newOwnerId: transferUserId() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to transfer ownership');
      }

      setShowTransfer(null);
      setTransferUserId('');
      refetchGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold">Groups</h1>
          <p class="text-gray-400 mt-1">
            Create and manage groups to share expressions with your team
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          class="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-500/20"
        >
          Create Group
        </button>
      </div>

      {/* Error Message */}
      <Show when={error()}>
        <div class="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <p class="text-red-400">{error()}</p>
        </div>
      </Show>

      {/* Pending Invitations */}
      <Show when={invitations()?.length}>
        <div class="mb-6">
          <h2 class="text-lg font-medium mb-3 flex items-center gap-2">
            <svg
              class="w-5 h-5 text-yellow-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
            Pending Invitations
          </h2>
          <div class="space-y-3">
            <For each={invitations()}>
              {(invitation) => (
                <div class="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <p class="font-medium">{invitation.groupName}</p>
                    <p class="text-sm text-gray-400">
                      Invited by {invitation.inviterEmail} on{' '}
                      {new Date(invitation.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div class="flex gap-2">
                    <button
                      onClick={() => acceptInvitation(invitation.id)}
                      class="px-4 py-2 bg-green-600 hover:bg-green-500 text-white font-medium rounded-lg transition-colors"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => rejectInvitation(invitation.id)}
                      class="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              )}
            </For>
          </div>
        </div>
      </Show>

      {/* Create Group Modal */}
      <Show when={showCreate()}>
        <div
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowCreate(false)}
        >
          <div
            class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 class="text-xl font-bold mb-4">Create Group</h2>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">Group Name</label>
                <input
                  type="text"
                  value={newGroupName()}
                  onInput={(e) => setNewGroupName(e.currentTarget.value)}
                  placeholder="Engineering Team"
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">
                  Description (optional)
                </label>
                <textarea
                  value={newGroupDesc()}
                  onInput={(e) => setNewGroupDesc(e.currentTarget.value)}
                  placeholder="A group for the engineering team..."
                  rows={3}
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                />
              </div>
            </div>

            <div class="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreate(false)}
                class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createGroup}
                disabled={loading() || !newGroupName().trim()}
                class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {loading() ? 'Creating...' : 'Create Group'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Invite Member Modal */}
      <Show when={showInvite()}>
        <div
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowInvite(null)}
        >
          <div
            class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 class="text-xl font-bold mb-4">Invite Member</h2>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1.5">Email Address</label>
              <input
                type="email"
                value={inviteEmail()}
                onInput={(e) => setInviteEmail(e.currentTarget.value)}
                placeholder="colleague@company.com"
                class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p class="text-sm text-gray-500 mt-2">
                The user must have an account to receive the invitation.
              </p>
            </div>

            <div class="flex gap-3 mt-6">
              <button
                onClick={() => setShowInvite(null)}
                class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={inviteMember}
                disabled={loading() || !inviteEmail().trim()}
                class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {loading() ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Transfer Ownership Modal */}
      <Show when={showTransfer()}>
        <div
          class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowTransfer(null)}
        >
          <div
            class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 class="text-xl font-bold mb-4">Transfer Ownership</h2>

            <div>
              <label class="block text-sm font-medium text-gray-300 mb-1.5">Select New Owner</label>
              <Show
                when={selectedGroupMembers().length > 0}
                fallback={
                  <p class="text-gray-500">No other members available. Invite members first.</p>
                }
              >
                <select
                  value={transferUserId()}
                  onChange={(e) => setTransferUserId(e.currentTarget.value)}
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Select a member...</option>
                  <For each={selectedGroupMembers()}>
                    {(member) => (
                      <option value={member.userId}>
                        {member.email} ({member.role})
                      </option>
                    )}
                  </For>
                </select>
              </Show>
              <p class="text-sm text-yellow-500 mt-2">
                Warning: You will become an admin after transferring ownership.
              </p>
            </div>

            <div class="flex gap-3 mt-6">
              <button
                onClick={() => setShowTransfer(null)}
                class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={transferOwnership}
                disabled={loading() || !transferUserId()}
                class="flex-1 px-4 py-2.5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
              >
                {loading() ? 'Transferring...' : 'Transfer Ownership'}
              </button>
            </div>
          </div>
        </div>
      </Show>

      {/* Groups Grid */}
      <Show
        when={!groups.loading}
        fallback={
          <div class="p-8 text-center">
            <svg
              class="animate-spin w-8 h-8 text-purple-500 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        }
      >
        <Show
          when={groups()?.length}
          fallback={
            <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-8 text-center">
              <svg
                class="w-12 h-12 text-gray-600 mx-auto mb-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <p class="text-gray-400">No groups yet</p>
              <p class="text-gray-500 text-sm mt-1">
                Create a group to share expressions with your team
              </p>
            </div>
          }
        >
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <For each={groups()}>
              {(group) => (
                <div class="bg-gray-800/50 border border-gray-700 rounded-xl p-5 hover:border-purple-500/50 transition-colors">
                  <div class="flex items-start justify-between mb-3">
                    <div class="flex items-center gap-3">
                      <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-medium">
                        {group.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h3 class="font-medium">{group.name}</h3>
                        <span class="text-xs text-purple-400 capitalize">{group.role}</span>
                      </div>
                    </div>
                    <div class="flex items-center gap-1">
                      {/* Invite button for admins/owners */}
                      <Show when={group.role === 'owner' || group.role === 'admin'}>
                        <button
                          onClick={() => setShowInvite(group.id)}
                          title="Invite member"
                          class="p-2 text-gray-500 hover:text-purple-400 transition-colors"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                            />
                          </svg>
                        </button>
                      </Show>
                      {/* Transfer ownership for owners */}
                      <Show when={group.role === 'owner'}>
                        <button
                          onClick={() => openTransferModal(group.id)}
                          title="Transfer ownership"
                          class="p-2 text-gray-500 hover:text-orange-400 transition-colors"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                            />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteGroup(group.id)}
                          title="Delete group"
                          class="p-2 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </Show>
                      {/* Leave button for non-owners */}
                      <Show when={group.role !== 'owner'}>
                        <button
                          onClick={() => leaveGroup(group.id)}
                          title="Leave group"
                          class="p-2 text-gray-500 hover:text-red-400 transition-colors"
                        >
                          <svg
                            class="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                            />
                          </svg>
                        </button>
                      </Show>
                    </div>
                  </div>

                  <Show when={group.description}>
                    <p class="text-gray-400 text-sm mb-3 line-clamp-2">{group.description}</p>
                  </Show>

                  <div class="flex items-center gap-4 text-sm text-gray-500">
                    <div class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m12 5.197v-1a6 6 0 00-3-5.197"
                        />
                      </svg>
                      {group.memberCount} members
                    </div>
                    <div class="flex items-center gap-1">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      {new Date(group.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
};

export default Groups;
